import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError } from "../../services/courseApi";
import {
  listOrganizations,
  updateOrganization,
} from "../../services/organizationApi";
import "../course/course.css";
import "./organization.css";

export default function OrganizationList() {
  const [organizations, setOrganizations] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const data = await listOrganizations(status ? { status } : {});
      setOrganizations(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(getApiError(error, "Could not load organizations"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const visibleOrganizations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return organizations;
    return organizations.filter((organization) =>
      String(organization.name || "").toLowerCase().includes(term)
    );
  }, [organizations, search]);

  async function toggleStatus(organization) {
    const nextStatus = organization.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const verb = nextStatus === "ACTIVE" ? "activate" : "deactivate";
    if (!window.confirm(`Are you sure you want to ${verb} “${organization.name}”?`)) return;
    setUpdatingId(organization.organization_id);
    try {
      await updateOrganization(organization.organization_id, { status: nextStatus });
      toast.success(`Organization ${nextStatus === "ACTIVE" ? "activated" : "deactivated"}`);
      await load();
    } catch (error) {
      toast.error(getApiError(error, `Could not ${verb} organization`));
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <main className="course-page">
      <div className="course-page-header">
        <div>
          <p className="course-eyebrow">Operations</p>
          <h1>Organizations</h1>
          <p className="text-muted mb-0">
            Manage course providers and their guru memberships.
          </p>
        </div>
        <Link className="btn btn-primary" to="/organizations/new">
          <i className="bi bi-plus-lg me-2" />Create organization
        </Link>
      </div>

      <section className="course-filter-card">
        <div className="organization-filter-grid">
          <label>
            <span>Search</span>
            <input
              className="form-control"
              placeholder="Organization name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label>
            <span>Status</span>
            <select
              className="form-select"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </label>
        </div>
      </section>

      <section className="course-table-card" aria-busy={loading}>
        {loading ? (
          <div className="course-state"><div className="spinner-border text-primary" /></div>
        ) : visibleOrganizations.length === 0 ? (
          <div className="course-state">
            <i className="bi bi-building-x fs-1 text-muted" />
            <h2>No organizations found</h2>
            <p className="text-muted">Change the filters or create an organization.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table course-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Active gurus</th>
                  <th>Status</th>
                  <th>Last updated</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrganizations.map((organization) => (
                  <tr key={organization.organization_id}>
                    <td>
                      <Link
                        className="course-title-link"
                        to={`/organizations/${organization.organization_id}`}
                      >
                        {organization.name}
                      </Link>
                      <div className="small text-muted">
                        Organization #{organization.organization_id}
                      </div>
                    </td>
                    <td>{organization.active_guru_count || 0}</td>
                    <td>
                      <span className={`organization-status ${organization.status?.toLowerCase()}`}>
                        {organization.status}
                      </span>
                    </td>
                    <td>
                      {organization.modified_at
                        ? new Date(organization.modified_at).toLocaleDateString("en-IN")
                        : "—"}
                    </td>
                    <td className="text-end text-nowrap">
                      <Link
                        className="btn btn-sm btn-outline-primary me-2"
                        to={`/organizations/${organization.organization_id}`}
                      >
                        View
                      </Link>
                      <Link
                        className="btn btn-sm btn-outline-secondary me-2"
                        to={`/organizations/${organization.organization_id}/edit`}
                      >
                        Edit
                      </Link>
                      <button
                        className={`btn btn-sm ${
                          organization.status === "ACTIVE"
                            ? "btn-outline-danger"
                            : "btn-outline-success"
                        }`}
                        disabled={updatingId === organization.organization_id}
                        onClick={() => toggleStatus(organization)}
                      >
                        {updatingId === organization.organization_id
                          ? "Saving…"
                          : organization.status === "ACTIVE"
                          ? "Deactivate"
                          : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
